import {
  forwardRef, useEffect, useId, useLayoutEffect, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import {
  IconeAlerte, IconeChevronBas, IconeCroix, IconeInfo, IconeOeil, IconeOeilBarre,
  IconeSucces, IconeVide,
} from './icones.tsx';

/**
 * Primitives d'interface.
 *
 * ── Ce qui fait la différence entre « fonctionnel » et « soigné » ───────────
 * Pas le dessin, mais la constance : un seul rayon, une seule durée de
 * transition, une seule façon d'indiquer le focus, une seule hauteur de
 * contrôle. Un formulaire où le bouton fait 38 px et le champ 40 px paraît
 * bâclé sans qu'on sache dire pourquoi.
 *
 * D'où les constantes ci-dessous : elles ne servent pas à éviter la
 * répétition, mais à rendre l'écart IMPOSSIBLE.
 *
 * ── À revoir quand la surface grandira ──────────────────────────────────────
 * Menus déroulants riches, combo-box, glisser-déposer : passer à Radix. Le
 * piégeage du focus et la navigation clavier de ces composants sont difficiles
 * à écrire correctement, et une erreur y rend l'application inutilisable au
 * clavier. Les primitives ci-dessous restent simples pour cette raison.
 */

/** Hauteur des contrôles. Un champ et un bouton alignés doivent coïncider. */
const HAUTEUR = {
  sm: 'h-8',
  md: 'h-[38px]',
  lg: 'h-11',
} as const;

/**
 * Anneau de focus, identique partout.
 *
 * `focus-visible` et non `focus` : un clic à la souris ne doit pas laisser un
 * anneau derrière lui, sans quoi les utilisateurs finissent par le trouver
 * parasite — et un jour quelqu'un le retire, ce qui rend l'application
 * inutilisable au clavier.
 */
const FOCUS = [
  'focus-visible:outline-none',
  'focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]',
  'focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]',
].join(' ');

/**
 * État désactivé, identique partout.
 *
 * Trois opacités différentes traînaient dans les composants — 40, 50 et 60 —
 * donnant trois gris pour un même état. Une opacité pose par ailleurs un
 * problème de fond : elle s'applique à tout, fond compris, si bien qu'un bouton
 * plein désactivé gardait sa couleur d'action, simplement pâlie.
 *
 * On repeint donc explicitement : fond neutre, encre grise, plus d'ombre. Le
 * bouton cesse de ressembler à une action possible.
 */
const DESACTIVE = [
  'disabled:cursor-not-allowed',
  'disabled:bg-[var(--color-disabled-surface)]',
  'disabled:text-[var(--color-disabled-fg)]',
  'disabled:border-[var(--color-border)]',
  'disabled:shadow-none',
].join(' ');

/** Transition unique. 150 ms : perceptible, jamais attendue. */
const TRANSITION =
  'transition-[background-color,border-color,color,box-shadow,opacity,transform] duration-150';

// ── Bouton ───────────────────────────────────────────────────────────────────

type VarianteBouton = 'primary' | 'secondary' | 'ghost' | 'danger' | 'discret';
type TailleBouton = 'sm' | 'md' | 'lg';

const CLASSES_BOUTON: Record<VarianteBouton, string> = {
  // Une ombre courte et un filet clair intérieur : c'est ce qui donne au
  // bouton principal l'aspect d'une touche plutôt que d'un rectangle coloré.
  primary: [
    'bg-[var(--color-primary)] text-[var(--color-primary-fg)]',
    'shadow-[var(--ombre-bouton)]',
    'hover:bg-[var(--color-primary-hover)]',
    // L'état pressé a sa propre couleur : un simple décalage d'un pixel ne se
    // voit pas sur un écran tactile, où il n'y a pas de survol pour l'annoncer.
    'active:translate-y-px active:bg-[var(--color-primary-active)]',
  ].join(' '),
  secondary: [
    'bg-[var(--color-surface)] text-[var(--color-fg)]',
    'border border-[var(--color-border-strong)]',
    'shadow-[var(--ombre-carte)]',
    'hover:bg-[var(--color-surface-hover)]',
    'active:translate-y-px',
  ].join(' '),
  ghost: [
    'bg-transparent text-[var(--color-fg-muted)]',
    'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
  ].join(' '),
  // Actions de ligne : encore plus effacé, pour ne pas concurrencer les
  // données du tableau.
  discret: [
    'bg-transparent text-[var(--color-fg-muted)]',
    'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
  ].join(' '),
  danger: [
    'bg-[var(--color-danger)] text-[var(--color-danger-fg)]',
    'shadow-[var(--ombre-bouton)]',
    // Une couleur, et non `opacity-90` : l'opacité laisse transparaître le fond
    // de la page, si bien que le même bouton n'avait pas la même teinte sur une
    // carte et sur un tableau.
    'hover:bg-[var(--color-danger-hover)] active:translate-y-px',
  ].join(' '),
};

/**
 * Classes d'un bouton, sans le `<button>`.
 *
 * Existe pour une seule raison : un lien de navigation doit parfois avoir
 * l'aspect d'un bouton — « Créer une année scolaire » dans un écran vide, par
 * exemple. Il doit rester un `<a>` (clic du milieu, nouvel onglet, copie
 * d'adresse), donc `Bouton` ne convient pas ; et recopier la liste de classes
 * à l'endroit d'appel la ferait diverger dès la première retouche du bouton.
 *
 * `packages/ui` ignore volontairement react-router : c'est à l'application de
 * fournir son composant de lien, et à celui-ci de venir chercher les classes.
 */
export function classesBouton({
  variante = 'primary', taille = 'md',
}: { variante?: VarianteBouton; taille?: TailleBouton } = {}): string {
  return [
    'relative inline-flex select-none items-center justify-center gap-1.5',
    'rounded-lg text-sm font-medium leading-none whitespace-nowrap no-underline',
    taille === 'sm' ? 'px-2.5' : 'px-3.5',
    HAUTEUR[taille],
    TRANSITION,
    FOCUS,
    CLASSES_BOUTON[variante],
  ].join(' ');
}

export interface ProprietesBouton extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBouton;
  taille?: TailleBouton;
  chargement?: boolean;
  /** Icône placée AVANT le libellé. Décorative : le texte reste obligatoire. */
  icone?: ReactNode;
  /** Bouton réduit à son icône. Exige alors un `aria-label`. */
  iconeSeule?: boolean;
}

export const Bouton = forwardRef<HTMLButtonElement, ProprietesBouton>(function Bouton(
  {
    variante = 'secondary', taille = 'md', chargement = false, icone,
    iconeSeule = false, disabled, children, className = '', type = 'button', ...reste
  },
  ref,
) {
  return (
    <button
      ref={ref}
      /*
       * `type="button"` par DÉFAUT.
       *
       * La valeur par défaut du HTML est « submit » : sans ce réglage, tout
       * bouton placé dans un formulaire le soumet, y compris « Annuler » ou
       * « Dupliquer ». La panne est silencieuse et se manifeste sur un écran
       * sans rapport. Les boutons qui doivent soumettre le déclarent.
       */
      type={type}
      // `disabled` seul retire l'élément de l'ordre de tabulation et rend
      // l'état invisible aux lecteurs d'écran pendant un chargement : on
      // annonce donc explicitement `aria-busy`.
      disabled={disabled || chargement}
      aria-busy={chargement || undefined}
      className={[
        'relative inline-flex select-none items-center justify-center gap-1.5',
        'rounded-lg text-sm font-medium leading-none whitespace-nowrap',
        iconeSeule ? 'aspect-square px-0' : taille === 'sm' ? 'px-2.5' : 'px-3.5',
        HAUTEUR[taille],
        TRANSITION,
        FOCUS,
        'disabled:pointer-events-none',
        DESACTIVE,
        CLASSES_BOUTON[variante],
        className,
      ].join(' ')}
      {...reste}
    >
      {chargement ? (
        <span
          aria-hidden="true"
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none"
        />
      ) : icone}
      {!iconeSeule && children}
      {iconeSeule && <span className="sr-only">{children}</span>}
    </button>
  );
});

// ── Base commune des champs ──────────────────────────────────────────────────

const CHAMP_BASE = [
  'w-full rounded-lg border text-sm',
  'border-[var(--color-border-strong)] bg-[var(--color-surface-input)]',
  'text-[var(--color-fg)] placeholder:text-[var(--color-fg-muted)]',
  'hover:border-[var(--color-fg-muted)]',
  TRANSITION,
  FOCUS,
  'focus-visible:border-[var(--color-focus)]',
  DESACTIVE,
].join(' ');

/** Bloc libellé + aide + erreur, commun à tous les champs. */
function Habillage({
  id, libelle, libelleMasque, aide, erreur, requis, children,
}: {
  id: string;
  libelle: string;
  libelleMasque?: boolean;
  aide?: string | undefined;
  erreur?: string | undefined;
  requis?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {/* `htmlFor` explicite : cliquer le libellé donne le focus au champ, et
          le lecteur d'écran annonce les deux ensemble. */}
      {/*
        * L'astérisque des champs requis est posé par CSS (`::after`), pas par
        * un élément.
        *
        * Deux raisons. Un lecteur d'écran annoncerait « astérisque » à chaque
        * champ, ce qui est du bruit — `required` sur l'input suffit à le
        * signaler. Et le contenu généré par CSS ne fait pas partie du texte du
        * libellé : « Adresse électronique » reste « Adresse électronique »,
        * pour l'utilisateur comme pour l'outil qui le cherche.
        */}
      <label
        htmlFor={id}
        data-requis={requis ? 'oui' : undefined}
        className={
          libelleMasque
            ? 'sr-only'
            : 'text-[13px] font-medium leading-none text-[var(--color-fg)] '
              + 'data-[requis=oui]:after:ml-0.5 data-[requis=oui]:after:text-[var(--color-danger)] '
              + "data-[requis=oui]:after:content-['*']"
        }
      >
        {libelle}
      </label>
      {children}
      {aide && !erreur && (
        <p id={`${id}-aide`} className="text-xs leading-snug text-[var(--color-fg-muted)]">
          {aide}
        </p>
      )}
      {erreur && (
        // `role="alert"` : l'erreur est annoncée dès son apparition, sans
        // attendre que l'utilisateur revienne sur le champ.
        <p
          id={`${id}-erreur`}
          role="alert"
          className="flex items-start gap-1 text-xs leading-snug text-[var(--color-danger)]"
        >
          <IconeAlerte taille={13} className="mt-px" />
          {erreur}
        </p>
      )}
    </div>
  );
}

function decrits(id: string, aide?: string, erreur?: string): string | undefined {
  return [erreur ? `${id}-erreur` : null, aide && !erreur ? `${id}-aide` : null]
    .filter(Boolean).join(' ') || undefined;
}

// ── Champ de saisie ──────────────────────────────────────────────────────────

export interface ProprietesChamp extends InputHTMLAttributes<HTMLInputElement> {
  libelle: string;
  libelleMasque?: boolean;
  erreur?: string | undefined;
  aide?: string | undefined;
  /** Icône affichée dans le champ, à gauche. Recherche, date… */
  icone?: ReactNode;
  taille?: TailleBouton;
}

export const Champ = forwardRef<HTMLInputElement, ProprietesChamp>(function Champ(
  {
    libelle, libelleMasque, erreur, aide, icone, taille = 'md', id,
    className = '', required, ...reste
  },
  ref,
) {
  const genere = useId();
  const idChamp = id ?? genere;
  /*
   * ── Un mot de passe peut s'afficher ───────────────────────────────────────
   * Tout champ `type="password"` porte un œil à droite : on vérifie ce qu'on a
   * tapé avant de valider, au lieu de recommencer après un refus. Le champ
   * repasse en masqué à chaque nouvel affichage de l'écran.
   */
  const motDePasse = reste.type === 'password';
  const [visible, setVisible] = useState(false);

  return (
    <Habillage
      id={idChamp}
      libelle={libelle}
      {...(libelleMasque !== undefined ? { libelleMasque } : {})}
      {...(aide !== undefined ? { aide } : {})}
      {...(erreur !== undefined ? { erreur } : {})}
      {...(required !== undefined ? { requis: required } : {})}
    >
      <div className="relative">
        {icone && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-fg-muted)]"
          >
            {icone}
          </span>
        )}
        <input
          ref={ref}
          id={idChamp}
          required={required}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={decrits(idChamp, aide, erreur)}
          className={[
            CHAMP_BASE,
            HAUTEUR[taille],
            icone ? 'pl-9' : 'pl-3',
            motDePasse ? 'pr-10' : 'pr-3',
            erreur ? 'border-[var(--color-danger)]' : '',
            className,
          ].join(' ')}
          {...reste}
          {...(motDePasse ? { type: visible ? 'text' : 'password' } : {})}
        />
        {motDePasse && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            aria-pressed={visible}
            aria-controls={idChamp}
            title={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            className={[
              'absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md',
              'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]',
              FOCUS,
            ].join(' ')}
          >
            {visible ? <IconeOeilBarre taille={16} /> : <IconeOeil taille={16} />}
          </button>
        )}
      </div>
    </Habillage>
  );
});

// ── Sélecteur ────────────────────────────────────────────────────────────────

export interface ProprietesSelecteur extends SelectHTMLAttributes<HTMLSelectElement> {
  libelle: string;
  libelleMasque?: boolean;
  aide?: string | undefined;
  erreur?: string | undefined;
  options: ReadonlyArray<{ valeur: string; texte: string }>;
  taille?: TailleBouton;
}

export const Selecteur = forwardRef<HTMLSelectElement, ProprietesSelecteur>(
  function Selecteur(
    {
      libelle, libelleMasque, options, aide, erreur, taille = 'md', id,
      className = '', ...reste
    },
    ref,
  ) {
    const genere = useId();
    const idChamp = id ?? genere;

    return (
      <Habillage
        id={idChamp}
        libelle={libelle}
        {...(libelleMasque !== undefined ? { libelleMasque } : {})}
        {...(aide !== undefined ? { aide } : {})}
        {...(erreur !== undefined ? { erreur } : {})}
      >
        <div className="relative">
          <select
            ref={ref}
            id={idChamp}
            aria-describedby={decrits(idChamp, aide, erreur)}
            className={[
              CHAMP_BASE,
              HAUTEUR[taille],
              // `appearance-none` + chevron dessiné : la flèche native diffère
              // sur chaque système, et c'est l'un des détails qui trahit le
              // plus vite une interface non finie.
              'cursor-pointer appearance-none pl-3 pr-9',
              className,
            ].join(' ')}
            {...reste}
          >
            {options.map((o) => (
              <option key={o.valeur} value={o.valeur}>{o.texte}</option>
            ))}
          </select>
          <IconeChevronBas
            taille={15}
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-fg-muted)]"
          />
        </div>
      </Habillage>
    );
  },
);

// ── Zone de texte ────────────────────────────────────────────────────────────

export interface ProprietesZoneTexte extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  libelle: string;
  /**
   * Libellé lu, mais pas affiché : pour une ligne d'une liste dont l'intitulé
   * est porté au-dessus — les objectifs d'une fiche, par exemple.
   */
  libelleMasque?: boolean;
  aide?: string | undefined;
  erreur?: string | undefined;
}

export const ZoneTexte = forwardRef<HTMLTextAreaElement, ProprietesZoneTexte>(
  function ZoneTexte(
    { libelle, libelleMasque, aide, erreur, id, className = '', rows = 3, required, ...reste },
    ref,
  ) {
    const genere = useId();
    const idChamp = id ?? genere;

    return (
      <Habillage
        id={idChamp}
        libelle={libelle}
        {...(libelleMasque !== undefined ? { libelleMasque } : {})}
        {...(aide !== undefined ? { aide } : {})}
        {...(erreur !== undefined ? { erreur } : {})}
        {...(required !== undefined ? { requis: required } : {})}
      >
        <textarea
          ref={ref}
          id={idChamp}
          rows={rows}
          required={required}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={decrits(idChamp, aide, erreur)}
          className={[
            CHAMP_BASE,
            'resize-y px-3 py-2 leading-relaxed',
            erreur ? 'border-[var(--color-danger)]' : '',
            className,
          ].join(' ')}
          {...reste}
        />
      </Habillage>
    );
  },
);

// ── Case à cocher ────────────────────────────────────────────────────────────

export function CaseACocher({
  libelle, description, ...reste
}: InputHTMLAttributes<HTMLInputElement> & {
  libelle: ReactNode;
  description?: ReactNode;
}) {
  const id = useId();
  const idDescription = `${id}-description`;
  return (
    <div className="flex items-start gap-2.5">
      <input
        id={id}
        type="checkbox"
        aria-describedby={description ? idDescription : undefined}
        className={[
          'mt-0.5 size-4 shrink-0 cursor-pointer rounded',
          'border border-[var(--color-border-strong)] bg-[var(--color-surface-input)]',
          'accent-[var(--color-primary)]',
          FOCUS,
        ].join(' ')}
        {...reste}
      />
      <div className="min-w-0">
        {/*
          * La description est HORS du libellé, reliée par `aria-describedby`.
          *
          * L'inclure ferait du nom de la case « Remplacer la liste actuelle
          * Sinon, les élèves sont ajoutés à ceux déjà présents » — une phrase
          * de vingt mots annoncée à chaque tabulation. Un libellé nomme, une
          * description explique : ce sont deux rôles distincts.
          */}
        <label
          htmlFor={id}
          className="cursor-pointer text-sm leading-snug text-[var(--color-fg)]"
        >
          {libelle}
        </label>
        {description && (
          <p id={idDescription} className="mt-0.5 text-xs text-[var(--color-fg-muted)]">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Carte ────────────────────────────────────────────────────────────────────

export function Carte({
  titre, description, action, pied, children, className = '',
  sansCadre = false, sansRembourrage = false, rangees,
}: {
  titre?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  pied?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Retire fond et contour : sert à grouper sans encadrer. */
  sansCadre?: boolean;
  /**
   * Retire le rembourrage du corps : pour un TABLEAU qui va au bord.
   *
   * Un tableau porte déjà ses propres bordures, et la carte lui en ajoutait une
   * seconde à vingt pixels de la première — un double cadre qui n'apporte rien
   * et coûte quarante pixels de largeur, soit une colonne étroite sur un écran
   * déjà à l'étroit. L'en-tête et le pied gardent le leur : ce sont du texte,
   * ils ont besoin de respirer.
   */
  sansRembourrage?: boolean;
  /**
   * Aligne le contenu sur celui des cartes VOISINES : le corps compte ce
   * nombre de rangées (un enfant direct par rangée), et chacune prend la
   * hauteur de la plus haute de la ligne.
   *
   * Pour des cartes posées côte à côte qui se comparent — deux offres, par
   * exemple. Sans cela, un prix sur trois lignes face à un prix sur une seule
   * décale tout ce qui suit, et rien ne se lit plus en face. Le parent doit
   * être une grille : la carte s'y inscrit en sous-grille (`subgrid`).
   */
  rangees?: number;
}) {
  const enTete = !!(titre || action);
  const alignee = rangees !== undefined && rangees > 0;
  return (
    <section
      className={[
        sansCadre
          ? ''
          : 'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] '
            + 'shadow-[var(--ombre-carte)]',
        alignee ? 'grid grid-rows-subgrid gap-0' : '',
        className,
      ].join(' ')}
      style={alignee
        ? { gridRow: `span ${rangees + (enTete ? 1 : 0) + (pied ? 1 : 0)}` }
        : undefined}
    >
      {(titre || action) && (
        <header
          className={[
            'flex flex-wrap items-start justify-between gap-3',
            sansCadre ? 'mb-3' : 'border-b border-[var(--color-border)] px-5 py-3.5',
          ].join(' ')}
        >
          <div className="min-w-0">
            {titre && (
              <h2 className="text-sm font-semibold leading-tight text-[var(--color-fg)]">
                {titre}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-xs leading-snug text-[var(--color-fg-muted)]">
                {description}
              </p>
            )}
          </div>
          {action && (
            <div className="flex flex-wrap items-center gap-2 print:hidden">{action}</div>
          )}
        </header>
      )}
      <div
        className={[
          sansCadre || sansRembourrage ? '' : 'p-5',
          alignee ? 'grid grid-rows-subgrid gap-0' : '',
        ].join(' ')}
        style={alignee ? { gridRow: `span ${rangees}` } : undefined}
      >
        {children}
      </div>
      {pied && (
        <footer className="rounded-b-xl border-t border-[var(--color-border)] bg-[var(--color-surface-raised)] px-5 py-3 text-sm">
          {pied}
        </footer>
      )}
    </section>
  );
}

// ── Message ──────────────────────────────────────────────────────────────────

type TonMessage = 'info' | 'succes' | 'attention' | 'erreur';

const STYLE_MESSAGE: Record<TonMessage, { fond: string; texte: string; icone: ReactNode }> = {
  info: {
    fond: 'bg-[var(--color-surface-raised)] border-[var(--color-border)]',
    texte: 'text-[var(--color-fg-muted)]',
    icone: <IconeInfo taille={15} />,
  },
  succes: {
    fond: 'bg-[var(--color-success-soft)] border-[var(--color-border)]',
    texte: 'text-[var(--color-success)]',
    icone: <IconeSucces taille={15} />,
  },
  attention: {
    fond: 'bg-[var(--color-warning-soft)] border-[var(--color-border)]',
    texte: 'text-[var(--color-warning)]',
    icone: <IconeAlerte taille={15} />,
  },
  erreur: {
    fond: 'bg-[var(--color-danger-soft)] border-[var(--color-border)]',
    texte: 'text-[var(--color-danger)]',
    icone: <IconeAlerte taille={15} />,
  },
};

export function Message({
  ton = 'info', titre, children,
}: {
  ton?: TonMessage;
  titre?: string;
  children: ReactNode;
}) {
  const style = STYLE_MESSAGE[ton];
  return (
    <div
      // Une erreur est annoncée immédiatement ; le reste attend une pause dans
      // la lecture, pour ne pas interrompre l'utilisateur en pleine saisie.
      role={ton === 'erreur' ? 'alert' : 'status'}
      className={[
        'flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm leading-snug',
        style.fond, style.texte,
      ].join(' ')}
    >
      <span aria-hidden="true" className="mt-px shrink-0">{style.icone}</span>
      <div className="min-w-0 flex-1">
        {titre && <p className="mb-0.5 font-medium">{titre}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
}

// ── Étiquette ────────────────────────────────────────────────────────────────

export function Etiquette({
  ton = 'info', children,
}: {
  ton?: TonMessage;
  children: ReactNode;
}) {
  const couleurs: Record<TonMessage, string> = {
    info: 'bg-[var(--color-surface-raised)] text-[var(--color-fg-muted)] ring-[var(--color-border)]',
    succes: 'bg-[var(--color-success-soft)] text-[var(--color-success)] ring-[var(--color-border)]',
    attention: 'bg-[var(--color-warning-soft)] text-[var(--color-warning)] ring-[var(--color-border)]',
    erreur: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)] ring-[var(--color-border)]',
  };
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5',
        'text-[11px] font-medium leading-5 ring-1 ring-inset',
        couleurs[ton],
      ].join(' ')}
    >
      {children}
    </span>
  );
}

// ── Dialogue modal ───────────────────────────────────────────────────────────

/**
 * Dialogues modaux actuellement ouverts.
 *
 * Une confirmation par-dessus un formulaire est un cas courant : fermer celle
 * du dessus ne doit pas rendre son défilement à la page tant que l'autre est là.
 */
let dialoguesOuverts = 0;

/**
 * Retire à la page sa barre de défilement le temps d'un dialogue.
 *
 * ── Ce que `showModal()` ne fait pas ────────────────────────────────────────
 * Il rend l'arrière-plan inerte, mais lui laisse sa barre. Signalé à
 * l'ouverture d'un créneau : « une double barre sur la droite ». Mesuré sur une
 * fenêtre de 1440 × 900 — la page derrière faisait 1 353 px de haut et gardait
 * la sienne, pendant que le panneau du dialogue affichait la sienne. Deux
 * barres côte à côte, dont une qui ne commande plus rien d'atteignable.
 *
 * La compensation en `padding-right` n'est pas un détail : retirer la barre
 * élargit la page d'autant, et tout le contenu sauterait à chaque ouverture.
 *
 * `overflow` est posé sur `<body>` : quand `<html>` est en `visible` — c'est
 * notre cas —, c'est de `<body>` que la fenêtre tient le sien.
 *
 * Renvoie de quoi défaire.
 */
function bloquerLeDefilement(): () => void {
  dialoguesOuverts += 1;
  if (dialoguesOuverts > 1) return () => { dialoguesOuverts -= 1; };

  const corps = document.body;
  const overflow = corps.style.overflow;
  const marge = corps.style.paddingRight;
  const barre = globalThis.innerWidth - document.documentElement.clientWidth;

  corps.style.overflow = 'hidden';
  if (barre > 0) corps.style.paddingRight = `${barre}px`;

  return () => {
    dialoguesOuverts -= 1;
    if (dialoguesOuverts > 0) return;
    corps.style.overflow = overflow;
    corps.style.paddingRight = marge;
  };
}

/**
 * Dialogue bâti sur l'élément natif `<dialog>`.
 *
 * C'est ce qui permet de se passer de Radix ici : `showModal()` fournit
 * gratuitement le piégeage du focus, l'inertie de l'arrière-plan et le rôle
 * ARIA — trois comportements qu'une réimplémentation manuelle rate, et dont
 * l'absence rend l'application inutilisable au clavier.
 *
 * ── `m-auto` n'est pas décoratif : sans lui, rien n'est centré ──────────────
 * Le navigateur centre un dialogue modal par `margin: auto`. La remise à zéro
 * de Tailwind (`*,::backdrop{margin:0}`) l'écrase, et toutes les boîtes se
 * collaient dans le coin supérieur gauche de la fenêtre — mesuré : centre à
 * 272×44 px au lieu de 640×360 sur un écran de 1280×720.
 *
 * ── Échap ferme, TOUJOURS, du premier coup ──────────────────────────────────
 * Échap était rendu inerte sur les formulaires. Signalé à l'usage : il fallait
 * le marteler, et le navigateur finissait par fermer la boîte DE LUI-MÊME —
 * après deux Échap refusés d'affilée, Chrome ne laisse plus la page s'y
 * opposer. L'écran, lui, la croyait toujours ouverte : le bouton qui l'avait
 * ouverte ne pouvait plus la rouvrir (« Programme », en programmation).
 *
 * Échap appelle donc `surFermeture` dès le premier appui, et toute fermeture
 * venue du navigateur est signalée de même (`close`) : l'état de l'écran et
 * celui de la boîte ne peuvent plus diverger.
 *
 * ── Le clic sur le fond, lui, reste STRICT par défaut ───────────────────────
 * Un formulaire à moitié rempli qui disparaît sur un clic à côté fait perdre
 * la saisie sans rien demander — le geste maladroit le plus fréquent. Échap
 * est un geste voulu ; le clic à côté, rarement.
 *
 * `fermetureLibre` ajoute le clic sur le fond et une croix dans l'en-tête. Il
 * est réservé aux dialogues où l'on TRAVAILLE sans rien valider — la liste des
 * élèves, les étapes d'une séance — et où il n'y a donc aucun bouton de
 * sortie.
 *
 * ── UNE seule barre de défilement ──────────────────────────────────────────
 * Signalé à l'usage : « des doubles barres inutiles » dans certains dialogues.
 * Mesuré sur « Insérer dans Français », fenêtre de 768 px : le corps plafonné
 * à 70vh défilait (653 px de contenu pour 538 visibles) PENDANT que la liste
 * du programme, plafonnée à 24 rem, défilait aussi. Deux barres côte à côte,
 * et une molette qui fait défiler tantôt l'une, tantôt l'autre.
 *
 * Le dialogue est donc une colonne plafonnée à la hauteur de la fenêtre, dont
 * le corps est lui-même une colonne qui rétrécit. Une liste longue s'y écrit
 * `classeListeDialogue`, dans des parents `min-h-0` : c'est ELLE qui rétrécit
 * et défile, pendant que le champ de recherche au-dessus et les boutons
 * au-dessous restent en place — et le corps n'a plus rien à faire défiler.
 * Un dialogue sans liste ne change pas : ses enfants ne rétrécissent pas, et
 * le corps défile seul s'il dépasse, sur un très petit écran.
 *
 * Le dialogue lui-même ne défile jamais : le navigateur lui donne
 * `overflow: auto`, ce qui ferait une troisième barre dès que l'en-tête et le
 * corps dépassent d'un pixel.
 */
export function Dialogue({
  titre, description, ouvert, surFermeture, largeur = 'md',
  fermetureLibre = false, children,
}: {
  titre: string;
  description?: string;
  ouvert: boolean;
  surFermeture: () => void;
  largeur?: 'sm' | 'md' | 'lg';
  /**
   * Autorise Échap, le clic sur le fond et une croix d'en-tête.
   *
   * À n'employer que sur un dialogue SANS bouton de sortie propre : sans lui,
   * il n'y aurait plus aucun moyen d'en sortir.
   */
  fermetureLibre?: boolean;
  children: ReactNode;
}) {
  const reference = useRef<HTMLDialogElement>(null);
  const declencheur = useRef<Element | null>(null);
  const idTitre = useId();
  const [zoom, setZoom] = useState(1);

  /*
   * ── La hauteur de la fenêtre, CORRIGÉE du zoom de l'affichage ─────────────
   * Le réglage « Taille d'affichage » pose `zoom` sur le contenu, et un
   * dialogue ouvert depuis un écran en hérite. Or `zoom` agrandit aussi les
   * unités de fenêtre : mesuré à 125 %, `calc(100dvh - 2rem)` donnait 920 px
   * dans une fenêtre de 768, et « Fermer » sortait de l'écran sans qu'aucune
   * barre ne permette de l'atteindre.
   *
   * Le zoom EFFECTIF de la boîte, et non la valeur du réglage : un dialogue
   * monté hors du contenu — depuis la barre latérale — n'est pas zoomé, et
   * diviser sa hauteur par 1,25 le raccourcirait pour rien.
   *
   * Lu sur le PARENT : la boîte n'est pas encore affichée à cet instant — elle
   * le sera par `showModal()` —, et un élément non affiché déclare un zoom de 1
   * quel que soit celui dont il héritera.
   */
  useLayoutEffect(() => {
    const parent = reference.current?.parentElement as (HTMLElement & { currentCSSZoom?: number }) | null;
    if (ouvert && parent) setZoom(parent.currentCSSZoom ?? 1);
  }, [ouvert]);

  useEffect(() => {
    const dialogue = reference.current;
    if (!dialogue) return;
    if (ouvert && !dialogue.open) {
      declencheur.current = document.activeElement;
      dialogue.showModal();
    } else if (!ouvert && dialogue.open) {
      dialogue.close();
      // Le focus doit revenir d'où il venait : sans cela, il repart au début
      // du document et l'utilisateur au clavier a perdu sa place.
      (declencheur.current as HTMLElement | null)?.focus?.();
    }
  }, [ouvert]);

  // La page derrière ne défile plus, et ne montre donc plus sa barre.
  useEffect(() => (ouvert ? bloquerLeDefilement() : undefined), [ouvert]);

  if (!ouvert) return null;

  const largeurs = { sm: '26rem', md: '34rem', lg: '48rem' };

  return (
    <dialog
      ref={reference}
      /*
       * `cancel` couvre la touche Échap. La fermeture passe par
       * `surFermeture` : c'est l'écran qui décide, et la boîte suit son état.
       */
      onCancel={(e) => { e.preventDefault(); surFermeture(); }}
      /*
       * Une fermeture que la page n'a pas demandée — le navigateur qui passe
       * outre — doit tout de même remettre l'écran d'accord avec la boîte.
       */
      onClose={() => { if (ouvert) surFermeture(); }}
      onClick={(e) => {
        if (fermetureLibre && e.target === reference.current) surFermeture();
      }}
      aria-labelledby={idTitre}
      style={{
        width: `min(${largeurs[largeur]}, calc(100vw - 2rem))`,
        maxHeight: zoom === 1 ? 'calc(100dvh - 2rem)' : `calc((100dvh - 2rem) / ${zoom})`,
      }}
      className={[
        // `m-auto` : le centrage natif d'un dialogue modal, que la remise à
        // zéro de Tailwind supprime. Sans lui, tout se colle en haut à gauche.
        'm-auto rounded-xl p-0 backdrop:bg-[var(--color-overlay)]',
        // `open:` et non `flex` tout court : un `display` posé sans condition
        // afficherait la boîte avant `showModal()`, hors de la couche modale.
        'overflow-hidden open:flex open:flex-col',
        'border border-[var(--color-border)] bg-[var(--color-surface)]',
        'text-[var(--color-fg)] shadow-[var(--ombre-flottante)]',
        'motion-safe:animate-[apparition_.16s_ease-out]',
      ].join(' ')}
    >
      <header className="flex shrink-0 items-start gap-3 border-b border-[var(--color-border)] px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 id={idTitre} className="text-[15px] font-semibold leading-tight">{titre}</h2>
          {description && (
            <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{description}</p>
          )}
        </div>
        {/*
          * La croix n'existe QUE dans le mode libre.
          *
          * Sur un formulaire, elle offrirait une troisième sortie à côté
          * d'« Annuler » et d'« Enregistrer » — celle qu'on clique sans y
          * penser, et qui fait perdre la saisie.
          */}
        {fermetureLibre && (
          <Bouton
            variante="ghost"
            taille="sm"
            iconeSeule
            icone={<IconeCroix taille={14} />}
            onClick={surFermeture}
            aria-label="Fermer"
          >
            Fermer
          </Bouton>
        )}
      </header>
      <div className="flex min-h-0 flex-col overflow-y-auto p-5">{children}</div>
    </dialog>
  );
}

/**
 * La liste longue d'un dialogue : elle prend la place qui reste, et défile.
 *
 * Ses parents jusqu'au corps du dialogue doivent être des colonnes `min-h-0`,
 * sans quoi rien ne rétrécit et le corps défile à sa place. Une liste garde le
 * plafond `max-h-*` qu'elle avait : elle ne rétrécit en dessous que si la
 * fenêtre l'exige. Le plancher garde quelques lignes visibles sur un très
 * petit écran.
 */
export const classeListeDialogue = 'min-h-32 shrink overflow-y-auto';

/** Barre d'actions d'un dialogue : toujours à droite, annuler avant valider. */
export function ActionsDialogue({ children }: { children: ReactNode }) {
  return (
    <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-[var(--color-border)] pt-4">
      {children}
    </div>
  );
}

// ── Bulle d'icône ────────────────────────────────────────────────────────────

/**
 * Pastille ronde portant une icône.
 *
 * Remplace les images d'icônes de l'ancienne application : six fichiers PNG à
 * charger, à redimensionner et à redessiner pour chaque thème. Une bulle
 * dessinée en CSS suit la palette toute seule, reste nette à n'importe quelle
 * densité d'écran, et ne coûte aucune requête.
 *
 * ── L'animation se déclenche depuis le PARENT ───────────────────────────────
 * `group-hover` et non `hover` : survoler précisément une pastille de 40 px
 * pour obtenir une réaction est un jeu d'adresse. C'est la carte entière qui
 * est la cible ; la bulle ne fait que l'accuser.
 *
 * ── Toujours décorative ─────────────────────────────────────────────────────
 * `aria-hidden` : le libellé qui l'accompagne porte le sens. Une bulle
 * annoncée par un lecteur d'écran ne ferait que doubler ce libellé.
 */
export function BulleIcone({
  children, taille = 'md', ton = 'primaire',
}: {
  children: ReactNode;
  taille?: 'md' | 'lg';
  /** `neutre` pour un état vide, `primaire` pour une action à portée de clic. */
  ton?: 'primaire' | 'neutre';
}) {
  return (
    <span
      aria-hidden="true"
      className={[
        'grid shrink-0 place-items-center rounded-full',
        taille === 'lg' ? 'size-12' : 'size-10',
        ton === 'primaire'
          ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary-soft-fg)]'
          : 'bg-[var(--color-surface-raised)] text-[var(--color-fg-muted)]',
        'ring-1 ring-inset ring-[var(--color-border)]',
        'shadow-[var(--ombre-carte)]',
        // Le déplacement et l'ombre sont ce qui donne la sensation de relief :
        // la bulle se soulève de deux pixels et projette plus loin.
        'transition-[transform,box-shadow] duration-200 ease-out',
        'group-hover:-translate-y-0.5 group-hover:shadow-[var(--ombre-flottante)]',
        // Un déplacement, même de deux pixels, reste du mouvement : il est
        // supprimé — et pas seulement accéléré — pour qui l'a demandé.
        'motion-reduce:transform-none motion-reduce:transition-none',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

// ── État vide ────────────────────────────────────────────────────────────────

/**
 * Un écran sans données doit dire ce qu'il attend et comment y remédier.
 * « Aucun résultat » seul laisse croire à une panne.
 */
export function Vide({
  titre, children, action, icone,
}: {
  titre: string;
  children?: ReactNode;
  action?: ReactNode;
  icone?: ReactNode;
}) {
  return (
    <div className="group flex flex-col items-center gap-3 px-6 py-12 text-center">
      <BulleIcone taille="lg" ton="neutre">
        {icone ?? <IconeVide taille={20} />}
      </BulleIcone>
      <p className="text-sm font-medium text-[var(--color-fg)]">{titre}</p>
      {children && (
        <div className="max-w-md text-sm leading-relaxed text-[var(--color-fg-muted)]">
          {children}
        </div>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

// ── Squelettes de chargement ─────────────────────────────────────────────────

/**
 * Bloc pulsant affiché à la place du contenu pendant son chargement.
 *
 * ── Pourquoi pas « Chargement… » ────────────────────────────────────────────
 * Un texte de chargement déplace tout le contenu au moment où il disparaît :
 * l'utilisateur perd sa position, et clique parfois sur ce qui vient de
 * bouger. Un squelette occupe la place finale — rien ne saute quand les
 * données arrivent.
 */
export function Squelette({ className = '' }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={[
        'animate-pulse rounded-md bg-[var(--color-surface-hover)] motion-reduce:animate-none',
        className,
      ].join(' ')}
    />
  );
}

/** Squelette de tableau : autant de lignes que le contenu attendu. */
export function SqueletteTableau({
  lignes = 5, colonnes = 4,
}: {
  lignes?: number;
  colonnes?: number;
}) {
  return (
    <div role="status" aria-label="Chargement en cours" className="flex flex-col gap-3 py-1">
      {Array.from({ length: lignes }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          {Array.from({ length: colonnes }, (_, j) => (
            <Squelette
              key={j}
              // Largeurs inégales : des colonnes toutes identiques ressemblent
              // à un damier, pas à des données.
              className={`h-4 ${j === 0 ? 'w-8' : j === colonnes - 1 ? 'w-20' : 'flex-1'}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Illustration ─────────────────────────────────────────────────────────────

/**
 * Une capture d'écran légendée, telle que la documentation les montre.
 *
 * L'image s'ouvre en grand dans un nouvel onglet : réduite à la largeur d'une
 * colonne de texte, une capture de tableau devient illisible, et c'est le
 * détail qu'on y cherche. Le lien plutôt qu'une fenêtre par-dessus : le retour
 * arrière et le zoom du navigateur y fonctionnent d'office.
 *
 * Chargement différé : une page de documentation en porte plusieurs, et l'on
 * n'en lit souvent que le début.
 */
export function Illustration({
  source, legende, largeur = 'pleine',
}: {
  source: string;
  /** Sert aussi de texte de remplacement : ce que l'image montre, en une phrase. */
  legende: string;
  /** « etroite » pour une fenêtre isolée, qui n'a pas à occuper toute la colonne. */
  largeur?: 'pleine' | 'etroite';
}) {
  return (
    <figure className={`flex flex-col gap-2 ${largeur === 'etroite' ? 'max-w-[34rem]' : ''}`}>
      <a
        href={source}
        target="_blank"
        rel="noreferrer"
        className="block overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] shadow-[var(--ombre-carte)] transition-colors hover:border-[var(--color-border-strong)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
      >
        <img src={source} alt={legende} loading="lazy" decoding="async" className="block h-auto w-full" />
      </a>
      <figcaption className="text-[13px] leading-snug text-[var(--color-fg-muted)]">
        {legende}
      </figcaption>
    </figure>
  );
}
